import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { MessService } from '../mess.service';
import { ToastmessageService } from 'src/app/toastmessage.service';

interface MonthlySummary {
  closed: number;
  pending: number;
  holiday: number;
  notSubmitted: number;
}

export interface AdminEntityView {
  entityCode: string;
  kitchensCount: number;
  summary: MonthlySummary;
}

export interface AdminDivisionView {
  divisionName: string;
  summary: MonthlySummary;
  kitchensCount: number;
  entities: AdminEntityView[];
  expanded: boolean;
}

@Component({
  selector: 'app-admin-dashboard',
  templateUrl: './admin-dashboard.component.html',
  styleUrls: ['./admin-dashboard.component.scss'],
})
export class AdminDashboardComponent implements OnInit {
  selectedMonth = moment();
  monthLabel = '';
  overviewPickerOpen = false;
  overviewPickerView: 'year' | 'month' = 'month';
  pickerMonth = moment().month();
  pickerYear = moment().year();
  yearPageStart = moment().year() - 5;
  readonly monthPickerOptions = moment.monthsShort().map((label, value) => ({ value, label }));

  monthlySummary: MonthlySummary = { closed: 0, pending: 0, holiday: 0, notSubmitted: 0 };
  totalKitchens = 0;
  summaryTrendUp = {
    closed: false,
    pending: false,
    holiday: false,
    notSubmitted: false,
    totalKitchens: false,
  };

  divisions: AdminDivisionView[] = [];

  constructor(
    private service: MessService,
    private spinner: NgxSpinnerService,
    private router: Router,
    private toast: ToastmessageService,
  ) {}

  ngOnInit(): void {
    this.syncPickerFromSelectedMonth();
    this.syncMonthLabel();
    this.loadAdminReports();
  }

  get summaryTotal(): number {
    const s = this.monthlySummary;
    return (s.closed ?? 0) + (s.pending ?? 0) + (s.holiday ?? 0) + (s.notSubmitted ?? 0);
  }

  summaryPercent(value: number, total?: number): string {
    const base = total ?? this.summaryTotal;
    if (!base) {
      return '0%';
    }
    return ((Number(value ?? 0) / base) * 100).toFixed(1) + '%';
  }

  divisionSummaryTotal(division: AdminDivisionView): number {
    const s = division.summary;
    return (s.closed ?? 0) + (s.pending ?? 0) + (s.holiday ?? 0) + (s.notSubmitted ?? 0);
  }

  entitySummaryTotal(entity: AdminEntityView): number {
    const s = entity.summary;
    return (s.closed ?? 0) + (s.pending ?? 0) + (s.holiday ?? 0) + (s.notSubmitted ?? 0);
  }

  isSummaryTrendUp(key: keyof typeof this.summaryTrendUp): boolean {
    return !!this.summaryTrendUp[key];
  }

  toggleDivision(division: AdminDivisionView): void {
    division.expanded = !division.expanded;
  }

  trackDivision(_index: number, division: AdminDivisionView): string {
    return division.divisionName;
  }

  trackEntity(_index: number, entity: AdminEntityView): string {
    return entity.entityCode;
  }

  viewKitchens(division: AdminDivisionView, event: Event): void {
    event.stopPropagation();
    const month = String(this.selectedMonth.month() + 1).padStart(2, '0');
    const year = this.selectedMonth.year();
    const queryParams: Record<string, string | number> = {
      month,
      year,
      divisionName: division.divisionName,
    };

    // Landing shell: list → division report via setView (same pattern as mess-do-details)
    if (this.router.url.includes('MessDetails')) {
      this.service.setAdminDivisionReportParams(queryParams);
      this.service.setView('adminDivisionReport');
      return;
    }

    this.router.navigate(['/adminDivisionReport'], { queryParams });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.overviewPickerOpen) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target && !target.closest('.overview-date-picker')) {
      this.overviewPickerOpen = false;
    }
  }

  toggleOverviewPicker(event?: Event): void {
    event?.stopPropagation();
    this.overviewPickerOpen = !this.overviewPickerOpen;
    if (this.overviewPickerOpen) {
      this.overviewPickerView = 'month';
      this.syncPickerFromSelectedMonth();
      this.alignYearPageToPicker();
    }
  }

  get yearPageYears(): number[] {
    return Array.from({ length: 12 }, (_, index) => this.yearPageStart + index);
  }

  get yearPageEnd(): number {
    return this.yearPageStart + 11;
  }

  showYearPickerView(): void {
    this.overviewPickerView = 'year';
    this.alignYearPageToPicker();
  }

  prevYearPage(): void {
    this.yearPageStart -= 12;
  }

  nextYearPage(): void {
    this.yearPageStart += 12;
  }

  selectPickerYear(year: number): void {
    this.pickerYear = Number(year);
    this.overviewPickerView = 'month';
    this.applyPickerSelection(false);
  }

  selectPickerMonth(month: number): void {
    this.pickerMonth = Number(month);
    this.applyPickerSelection(true);
  }

  private alignYearPageToPicker(): void {
    this.yearPageStart = this.pickerYear - 5;
  }

  private syncPickerFromSelectedMonth(): void {
    this.pickerMonth = this.selectedMonth.month();
    this.pickerYear = this.selectedMonth.year();
    this.alignYearPageToPicker();
  }

  private applyPickerSelection(closePanel: boolean): void {
    const picked = moment({ year: this.pickerYear, month: this.pickerMonth, day: 1 });
    if (!picked.isValid()) {
      return;
    }
    const monthChanged = !picked.clone().startOf('month').isSame(this.selectedMonth, 'month');
    this.selectedMonth = picked.clone().startOf('month');
    this.syncMonthLabel();
    if (closePanel) {
      this.overviewPickerOpen = false;
    }
    if (monthChanged) {
      this.loadAdminReports();
    }
  }

  private syncMonthLabel(): void {
    this.monthLabel = this.selectedMonth.format('MMM YYYY');
  }

  private syncSelectedMonthFromReport(monthText: string, year: number): void {
    const trimmed = String(monthText ?? '').trim();
    if (!trimmed) {
      return;
    }
    const withYear = trimmed.match(/\d{4}/) ? trimmed : `${trimmed} ${year}`;
    const parsed = moment(withYear, ['MMM YYYY', 'MMMM YYYY', 'MM YYYY'], true);
    if (parsed.isValid()) {
      this.selectedMonth = parsed.clone().startOf('month');
      this.syncPickerFromSelectedMonth();
      this.syncMonthLabel();
    }
  }

  loadAdminReports(): void {
    const month = this.selectedMonth.month() + 1;
    const year = this.selectedMonth.year();
    this.spinner.show();
    this.service.adminReports(month, year).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res ?? {};
        const yearFromPicker = this.selectedMonth.year();
        this.syncSelectedMonthFromReport(String(data.month ?? ''), yearFromPicker);
        this.monthLabel = this.selectedMonth.format('MMM YYYY');

        const summary = data.summary ?? {};
        const nextSummary: MonthlySummary = {
          closed: Number(summary.closed ?? 0),
          pending: Number(summary.pending ?? 0),
          holiday: Number(summary.holiday ?? 0),
          notSubmitted: Number(summary.notSubmitted ?? 0),
        };

        const divisionList = Array.isArray(data.divisions) ? data.divisions : [];
        this.divisions = divisionList.map((division: any, index: number) =>
          this.mapDivision(division, index === 0),
        );
        this.totalKitchens = this.calculateTotalKitchens(this.divisions);
        this.monthlySummary = nextSummary;
        this.updateSummaryTrendsFromResponse(nextSummary, this.totalKitchens);
        this.spinner.hide();
      },
      error: (error: any) => {
        this.monthlySummary = { closed: 0, pending: 0, holiday: 0, notSubmitted: 0 };
        this.totalKitchens = 0;
        this.divisions = [];
        this.updateSummaryTrendsFromResponse(this.monthlySummary, 0);
        this.toast.showError(error.error.message || 'Something went wrong');
        this.spinner.hide();
      },
    });
  }

  private mapSummary(raw: any): MonthlySummary {
    return {
      closed: Number(raw?.closed ?? 0),
      pending: Number(raw?.pending ?? 0),
      holiday: Number(raw?.holiday ?? 0),
      notSubmitted: Number(raw?.notSubmitted ?? 0),
    };
  }

  private mapDivision(division: any, expanded: boolean): AdminDivisionView {
    const entities = (Array.isArray(division?.entities) ? division.entities : []).map((entity: any) => ({
      entityCode: String(entity?.entityCode ?? '').trim(),
      kitchensCount: Number(entity?.kitchensCount ?? 0),
      summary: this.mapSummary(entity?.summary ?? {}),
    }));
    const kitchensCount = entities.reduce(
      (sum: number, entity: AdminEntityView) => sum + Number(entity.kitchensCount ?? 0),
      0,
    );
    return {
      divisionName: String(division?.divisionName ?? '').trim(),
      summary: this.mapSummary(division?.summary ?? {}),
      kitchensCount,
      entities,
      expanded,
    };
  }

  private calculateTotalKitchens(divisions: AdminDivisionView[]): number {
    return divisions.reduce(
      (sum, division) => sum + division.entities.reduce((entitySum, entity) => entitySum + entity.kitchensCount, 0),
      0,
    );
  }

  private updateSummaryTrendsFromResponse(summary: MonthlySummary, totalKitchens: number): void {
    this.summaryTrendUp = {
      closed: Number(summary.closed ?? 0) > 0,
      pending: Number(summary.pending ?? 0) > 0,
      holiday: Number(summary.holiday ?? 0) > 0,
      notSubmitted: Number(summary.notSubmitted ?? 0) > 0,
      totalKitchens: Number(totalKitchens ?? 0) > 0,
    };
  }
}
